import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { MessageSquare, Loader2, Save, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "var(--brand)";

export default function MessagingSettings() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [sender, setSender] = useState("");
  const [saving, setSaving] = useState(false);
  const [testMobile, setTestMobile] = useState("");
  const [testing, setTesting] = useState(false);

  const load = () => api.kudosityConfig().then((s) => { setStatus(s); setSender(s.sender || ""); }).catch(() => toast.error("Could not load messaging settings."));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim() || !apiSecret.trim() || !sender.trim()) { toast.error("Enter the API key, secret and sender."); return; }
    setSaving(true);
    try { const s = await api.saveKudosity({ api_key: apiKey.trim(), api_secret: apiSecret.trim(), sender: sender.trim() }); setStatus(s); setApiKey(""); setApiSecret(""); toast.success("Kudosity credentials saved."); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (!testMobile.trim()) { toast.error("Enter a mobile number to test."); return; }
    setTesting(true);
    try { await api.testKudosity({ mobile: testMobile.trim() }); toast.success("Test SMS sent — check the handset."); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed."); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="messaging-settings">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-brand font-semibold text-slate-800">Messaging</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Configure the SMS provider used to send document signing links and reminders. Keys are stored securely in the app — no redeploy needed.</p>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800">Kudosity (SMS)</span>
            {status?.configured
              ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="kudosity-status"><CheckCircle2 className="h-3 w-3 mr-1" />Configured</Badge>
              : <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]" data-testid="kudosity-status">Not configured</Badge>}
          </div>
          {status?.key_hint && <span className="text-[11px] text-slate-400">Key: {status.key_hint}</span>}
        </div>

        <div>
          <Label className="text-[11px] font-semibold text-slate-600">API key</Label>
          <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={status?.configured ? "•••••••• (enter to replace)" : "Kudosity API key"} className="mt-1 h-9 text-sm" data-testid="kudosity-key" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">API secret</Label>
          <Input type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} placeholder={status?.configured ? "•••••••• (enter to replace)" : "Kudosity API secret"} className="mt-1 h-9 text-sm" data-testid="kudosity-secret" />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">Sender (registered number or ≤11-char sender ID)</Label>
          <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="e.g. Prydes or 61400000000" maxLength={11} className="mt-1 h-9 text-sm" data-testid="kudosity-sender" />
        </div>
        <Button onClick={save} disabled={saving} style={{ backgroundColor: CRIMSON }} data-testid="kudosity-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save credentials
        </Button>
      </Card>

      {status?.configured && (
        <Card className="p-5 space-y-3 mt-4">
          <div className="font-semibold text-slate-800 text-sm">Send a test SMS</div>
          <div className="flex gap-2">
            <Input value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="04xx xxx xxx" className="h-9 text-sm" data-testid="kudosity-test-mobile" />
            <Button onClick={test} disabled={testing} variant="outline" className="h-9 whitespace-nowrap" data-testid="kudosity-test-send">
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Send test
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
